export type Point = {
    x: number;
    y: number;
};

//
// Using math from https://www.geeksforgeeks.org/check-if-two-given-line-segments-intersect/
//

// Given three colinear points p, q, r, the function checks if point q lies on line segment 'pr'
const isOnSegment = (p: Point, q: Point, r: Point) =>
    q.x <= Math.max(p.x, r.x) && q.x >= Math.min(p.x, r.x) &&
    q.y <= Math.max(p.y, r.y) && q.y >= Math.min(p.y, r.y)
;

enum Orientation {
    colinear = 0,
    clockwise = 1,
    counterClockwise = 2,
}

// To find orientation of ordered triplet (p, q, r).
const getOrientation = (p: Point, q: Point, r: Point) => {
    // Recall we need to invert y because screen coordinates are upside down
    const val = (-q.y + p.y) * (r.x - q.x) - (q.x - p.x) * (-r.y + q.y);
    if (val === 0) {
        return Orientation.colinear;
    }
    return val > 0 ? Orientation.clockwise : Orientation.counterClockwise;
};

// The main function that returns true if line segment 'p1q1' and 'p2q2' intersect.
export const doLinesIntersect = (p1: Point, q1: Point, p2: Point, q2: Point) => {
    // Find the four orientations needed for general and special cases
    const o1 = getOrientation(p1, q1, p2);
    const o2 = getOrientation(p1, q1, q2);
    const o3 = getOrientation(p2, q2, p1);
    const o4 = getOrientation(p2, q2, q1);

    // General case
    if (o1 !== o2 && o3 !== o4) return true;

    // Special Cases
    // p1, q1 and p2 are colinear and p2 lies on segment p1q1
    if (o1 === Orientation.colinear && isOnSegment(p1, p2, q1)) return true;

    // p1, q1 and p2 are colinear and q2 lies on segment p1q1
    if (o2 === Orientation.colinear && isOnSegment(p1, q2, q1)) return true;

    // p2, q2 and p1 are colinear and p1 lies on segment p2q2
    if (o3 === Orientation.colinear && isOnSegment(p2, p1, q2)) return true;

    // p2, q2 and q1 are colinear and q1 lies on segment p2q2
    if (o4 === Orientation.colinear && isOnSegment(p2, q1, q2)) return true;

    return false; // Doesn't fall in any of the above cases
};



// WEBPACK FOOTER //
// ./src/logic/reports/doLinesIntersect.ts